import React, { MouseEvent } from 'react';

import classNames from 'classnames';

import { Dislike } from '@uikit/icons/Dislike';
import { FilledHeart2 } from '@uikit/icons/FilledHeart2';
import { Like } from '@uikit/icons/Like';

import { ReviewContentReviewData } from '@shared/api/types/review';
import { useAuth } from '@shared/common/providers/AuthProvider';

import useReviewFeedbackLike from './useReviewFeedbackLike';
import useReviewFeedbackVote from './useReviewFeedbackVote';

interface ReviewFeedbackProps {
  isHorizontal?: boolean;
  review: ReviewContentReviewData;
}

const formatPeopleCount = (count: number) => (count === 1 ? '1 person' : `${count} people`);

const ReviewFeedback = ({ isHorizontal, review }: ReviewFeedbackProps) => {
  const { isAuth } = useAuth();
  const [like, setLike] = useReviewFeedbackLike({ review });
  const [vote, setVote] = useReviewFeedbackVote({ review });
  const positiveVotes =
    (review.positiveVotes || 0) - Number(review.userVote === 1) + Number(vote === 1);
  const funnyVotes = (review.funnyVotes || 0) - Number(review.userFunnyVote) + Number(like);

  const handleYesButtonClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setVote((oldVote) => (oldVote === 1 ? 0 : 1));
  };

  const handleNoButtonClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setVote((oldVote) => (oldVote === -1 ? 0 : -1));
  };

  const handleFunnyButtonClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setLike((oldLike) => !oldLike);
  };

  return (
    <div
      className={classNames('flex gap-2', {
        'flex-col': !isHorizontal,
        'justify-between items-center flex-wrap': isHorizontal,
      })}
    >
      <div className="text-neutral-400 text-xs flex flex-col gap-1">
        <span>Was this review helpful?</span>

        {isHorizontal && (
          <span className="font-light">
            {formatPeopleCount(positiveVotes)} found this review helpful
          </span>
        )}
      </div>

      {isAuth && (
        <div
          className={classNames('flex flex-wrap gap-1', {
            'max-w-[255px]': isHorizontal,
          })}
        >
          <button
            className={classNames(
              'button button-sm px-2.5 min-w-[4.375rem] font-normal rounded-[4px] gap-2 transition-colors',
              {
                'bg-[#F3F3F6] text-primary-700': vote !== 1,
                'bg-primary-700 text-neutral-50': vote === 1,
              },
            )}
            onClick={handleYesButtonClick}
          >
            <Like className="icon-xs" />
            Yes
          </button>
          <button
            className={classNames(
              'button button-sm px-2.5 min-w-[4.375rem] font-normal rounded-[4px] gap-2 transition-colors',
              {
                'bg-[#F3F3F6] text-secondary-700': vote !== -1,
                'bg-secondary-700 text-neutral-50': vote === -1,
              },
            )}
            onClick={handleNoButtonClick}
          >
            <Dislike className="icon-xs" />
            No
          </button>
          <button
            className={classNames(
              'button button-sm px-2.5 max-w-36 font-normal rounded-[4px] gap-2 flex-1 transition-colors',
              {
                'bg-neutral-600 text-neutral-50': like,
                'bg-[#F3F3F6] text-neutral-600': !like,
              },
            )}
            onClick={handleFunnyButtonClick}
          >
            <FilledHeart2 className="icon-xs" />
            Funny
          </button>
        </div>
      )}
      {!isHorizontal && (
        <span className="text-neutral-400 text-xs font-light">
          {formatPeopleCount(positiveVotes)} found this review helpful
          <br />
          {formatPeopleCount(funnyVotes)} found this review funny
        </span>
      )}
    </div>
  );
};

export default ReviewFeedback;
